import { PlayerAPI, PlayerEvent, PlayerEventBase } from 'bitmovin-player';
import { isPlayerAlive, safePlayerOff } from '../../../helpers/playerGuards';

interface Listener {
    el: PlayerAPI;
    fn: (e: PlayerEventBase) => void;
    e: PlayerEvent;
}

export type AttachPlayerEventsCallback = (
    lastEvent: PlayerEvent | null,
    event: PlayerEvent,
    e: PlayerEventBase,
    firstPlay?: boolean
) => void;

/**
 * Creates an event attachment helper bound to a specific Bitmovin player instance.
 *
 * The returned function registers listeners for a list of player events and returns
 * a cleanup function that detaches all registered listeners.
 *
 * @param player - Bitmovin player instance used for subscribing/unsubscribing events.
 * @returns A function that attaches events and provides a detach callback.
 */
const attachEvents = (player: PlayerAPI) => {
    return function attachPlayerEvents(events: PlayerEvent[], callback: AttachPlayerEventsCallback) {
        const listeners: Listener[] = [];
        let firstPlay = true;
        let lastEvent: PlayerEvent | null = null;

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        if (!player || !events) return () => {};

        events.forEach((event) => {
            const listener = (e: PlayerEventBase) => {
                if (['sourceloaded', 'destroy', 'playbackfinished', 'unload'].includes(event)) {
                    firstPlay = true;
                }
                if (event === 'playing') {
                    callback(lastEvent, event, e, firstPlay);
                    firstPlay = false;
                } else {
                    callback(lastEvent, event, e);
                }
                lastEvent = event;
            };
            player?.on(event, listener);
            listeners.push({ el: player, fn: listener, e: event });
        });

        return function detachEvents() {
            listeners.forEach((l) => {
                // Skip listeners whose player instance has already been destroyed;
                // calling `off` on it throws API_NOT_AVAILABLE (error 1001).
                if (l && l.el && typeof l.el.off === 'function' && isPlayerAlive(l.el)) {
                    safePlayerOff(l.el, l.e, l.fn);
                }
            });
        };
    };
};

export default attachEvents;
